/**
 * Filters of the audit dashboard: defaults, URL query string (bookmarkable view), backend parameters, chips.
 * Pure functions (no Angular), covered by audit-query.spec.ts.
 */
import { AuditDocAction, AuditExecutionStatus, AuditOperation } from './audit.models';

export type AuditTab = 'executions' | 'changes' | 'documents';
export type AuditPeriod = '24h' | '7d' | '30d' | '90d' | 'custom';

export interface AuditFilters {
  tab: AuditTab;
  period: AuditPeriod;
  /** custom period, local yyyy-mm-ddThh:mm (to excluded), or whole days yyyy-mm-dd (to included) */
  from: string | null;
  to: string | null;
  q: string;
  /** executions */
  status: AuditExecutionStatus[];
  type: 'P' | 'R' | null;
  processId: number | null;
  /** both */
  userId: number | null;
  sessionId: number | null;
  /** changes */
  operation: AuditOperation[];
  tableId: number | null;
  recordId: string | null;
  /** documents (none: views and downloads) */
  action: AuditDocAction[];
  page: number;
  pageSize: number;
  sort: 'desc' | 'asc';
}

export const PAGE_SIZES = [25, 50, 100];
export const PERIODS: AuditPeriod[] = ['24h', '7d', '30d', '90d', 'custom'];
const STATUSES: AuditExecutionStatus[] = ['R', 'S', 'F', 'C'];
const OPERATIONS: AuditOperation[] = ['I', 'U', 'D', 'STUDIO'];
export const DOC_ACTIONS: AuditDocAction[] = ['VIEW', 'DOWNLOAD', 'THUMBNAIL'];
const TABS: AuditTab[] = ['executions', 'changes', 'documents'];
const DAY = 86_400_000;

export function defaultFilters(): AuditFilters {
  return {
    tab: 'executions',
    period: '7d',
    from: null,
    to: null,
    q: '',
    status: [],
    type: null,
    processId: null,
    userId: null,
    sessionId: null,
    operation: [],
    tableId: null,
    recordId: null,
    action: [],
    page: 0,
    pageSize: 25,
    sort: 'desc',
  };
}

/** local yyyy-mm-dd of a date */
export function toLocalDay(date: Date): string {
  const p = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${p(date.getMonth() + 1)}-${p(date.getDate())}`;
}

/** local yyyy-mm-ddThh:mm of a date (minute precision) */
export function toLocalDateTime(date: Date): string {
  const p = (n: number) => String(n).padStart(2, '0');
  return `${toLocalDay(date)}T${p(date.getHours())}:${p(date.getMinutes())}`;
}

/** "yyyy-mm-dd" (whole day) or "yyyy-mm-ddThh:mm", local time */
export function parseLocalMoment(value: string | null): { date: Date; dayOnly: boolean } | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})(?:T(\d{2}):(\d{2}))?$/.exec(value ?? '');
  if (!m) return null;
  const dayOnly = m[4] === undefined;
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]), dayOnly ? 0 : Number(m[4]), dayOnly ? 0 : Number(m[5]));
  if (isNaN(d.getTime()) || (!dayOnly && (Number(m[4]) > 23 || Number(m[5]) > 59))) return null;
  return { date: d, dayOnly };
}

/** Period [from, to): presets end now; a custom period is exact to the minute (a day-only end includes that day) */
export function periodRange(f: Pick<AuditFilters, 'period' | 'from' | 'to'>, now: Date): { from: Date; to: Date } {
  if (f.period === 'custom') {
    const from = parseLocalMoment(f.from);
    const end = parseLocalMoment(f.to);
    if (from && end) {
      const to = end.dayOnly ? new Date(end.date.getFullYear(), end.date.getMonth(), end.date.getDate() + 1) : end.date;
      if (from.date < to) return { from: from.date, to };
    }
  }
  const days = f.period === '24h' ? 1 : f.period === '30d' ? 30 : f.period === '90d' ? 90 : 7;
  return { from: new Date(now.getTime() - days * DAY), to: now };
}

const positiveInt = (v: string | null): number | null => (v && /^\d{1,9}$/.test(v) && Number(v) > 0 ? Number(v) : null);

function codes<T extends string>(v: string | null, allowed: readonly T[]): T[] {
  return (v ?? '')
    .split(',')
    .map((s) => s.trim())
    .filter((s): s is T => (allowed as readonly string[]).includes(s));
}

/** URL query string -> filters (unknown or invalid values fall back to the defaults) */
export function fromQueryParams(params: URLSearchParams): AuditFilters {
  const d = defaultFilters();
  const period = params.get('period') as AuditPeriod;
  const size = Number(params.get('size'));
  const page = Number(params.get('page'));
  const f: AuditFilters = {
    tab: TABS.includes(params.get('tab') as AuditTab) ? (params.get('tab') as AuditTab) : 'executions',
    period: PERIODS.includes(period) ? period : d.period,
    from: parseLocalMoment(params.get('from')) ? params.get('from') : null,
    to: parseLocalMoment(params.get('to')) ? params.get('to') : null,
    q: (params.get('q') ?? '').slice(0, 100),
    status: codes(params.get('status'), STATUSES),
    type: params.get('type') === 'P' || params.get('type') === 'R' ? (params.get('type') as 'P' | 'R') : null,
    processId: positiveInt(params.get('process')),
    userId: positiveInt(params.get('user')),
    sessionId: positiveInt(params.get('session')),
    operation: codes(params.get('op'), OPERATIONS),
    tableId: positiveInt(params.get('table')),
    recordId: params.get('record')?.slice(0, 200) || null,
    action: codes(params.get('act'), DOC_ACTIONS),
    page: Number.isInteger(page) && page > 0 ? page : 0,
    pageSize: PAGE_SIZES.includes(size) ? size : d.pageSize,
    sort: params.get('sort') === 'asc' ? 'asc' : 'desc',
  };
  if (f.period === 'custom' && (!f.from || !f.to)) f.period = d.period;
  return f;
}

/** filters -> URL query string, only what differs from the defaults */
export function toQueryParams(f: AuditFilters): URLSearchParams {
  const d = defaultFilters();
  const p = new URLSearchParams();
  if (f.tab !== d.tab) p.set('tab', f.tab);
  if (f.period !== d.period) p.set('period', f.period);
  if (f.period === 'custom') {
    if (f.from) p.set('from', f.from);
    if (f.to) p.set('to', f.to);
  }
  if (f.q.trim()) p.set('q', f.q.trim());
  if (f.status.length) p.set('status', f.status.join(','));
  if (f.type) p.set('type', f.type);
  if (f.processId) p.set('process', String(f.processId));
  if (f.userId) p.set('user', String(f.userId));
  if (f.sessionId) p.set('session', String(f.sessionId));
  if (f.operation.length) p.set('op', f.operation.join(','));
  if (f.tableId) p.set('table', String(f.tableId));
  if (f.recordId) p.set('record', f.recordId);
  if (f.action.length) p.set('act', f.action.join(','));
  if (f.page) p.set('page', String(f.page));
  if (f.pageSize !== d.pageSize) p.set('size', String(f.pageSize));
  if (f.sort !== d.sort) p.set('sort', f.sort);
  return p;
}

/** Backend parameters of the list of the current tab (only the filters of that tab) */
export function toApiParams(f: AuditFilters, now: Date): Record<string, string> {
  const { from, to } = periodRange(f, now);
  const p: Record<string, string> = {
    from: from.toISOString(),
    to: to.toISOString(),
    offset: String(f.page * f.pageSize),
    limit: String(f.pageSize),
    sort: f.sort,
  };
  if (f.q.trim()) p['q'] = f.q.trim();
  if (f.userId) p['userId'] = String(f.userId);
  if (f.tab === 'documents') {
    if (f.action.length) p['action'] = f.action.join(',');
    return p;
  }
  if (f.sessionId) p['sessionId'] = String(f.sessionId);
  if (f.tab === 'executions') {
    if (f.status.length) p['status'] = f.status.join(',');
    if (f.type) p['type'] = f.type;
    if (f.processId) p['processId'] = String(f.processId);
  } else {
    if (f.operation.length) p['operation'] = f.operation.join(',');
    if (f.tableId) p['tableId'] = String(f.tableId);
    if (f.recordId) p['recordId'] = f.recordId;
  }
  return p;
}

/** Filter keys that narrow the list of the current tab (period, paging and sort excluded) */
export type AuditFilterKey = 'q' | 'status' | 'type' | 'processId' | 'userId' | 'sessionId' | 'operation' | 'tableId' | 'recordId' | 'action';

export function activeFilterKeys(f: AuditFilters): AuditFilterKey[] {
  const keys: AuditFilterKey[] = [];
  if (f.q.trim()) keys.push('q');
  if (f.tab === 'executions') {
    if (f.status.length) keys.push('status');
    if (f.type) keys.push('type');
    if (f.processId) keys.push('processId');
  } else if (f.tab === 'changes') {
    if (f.operation.length) keys.push('operation');
    if (f.tableId) keys.push('tableId');
    if (f.recordId) keys.push('recordId');
  } else if (f.action.length) keys.push('action');
  if (f.userId) keys.push('userId');
  // no session on the document reads
  if (f.sessionId && f.tab !== 'documents') keys.push('sessionId');
  return keys;
}

/** Filters without the given key (page back to the first one) */
export function clearFilter(f: AuditFilters, key: AuditFilterKey): AuditFilters {
  const d = defaultFilters();
  return { ...f, [key]: d[key], page: 0 };
}

/** Filters of the current tab cleared; tab, period, page size and sort kept */
export function clearAllFilters(f: AuditFilters): AuditFilters {
  const d = defaultFilters();
  return { ...d, tab: f.tab, period: f.period, from: f.from, to: f.to, pageSize: f.pageSize, sort: f.sort };
}

/** "850 ms", "3.4 s", "2 min 05 s", "1 h 02 min", "3 d 4 h" */
export function formatDuration(ms: number | null | undefined): string {
  if (ms === null || ms === undefined || ms < 0 || !isFinite(ms)) return '';
  if (ms < 1000) return `${Math.round(ms)} ms`;
  const s = ms / 1000;
  if (s < 60) return `${s < 10 ? s.toFixed(1) : Math.round(s)} s`;
  const totalS = Math.round(s);
  const m = Math.floor(totalS / 60);
  if (m < 60) return `${m} min ${String(totalS % 60).padStart(2, '0')} s`;
  const h = Math.floor(m / 60);
  if (h < 48) return `${h} h ${String(m % 60).padStart(2, '0')} min`;
  return `${Math.floor(h / 24)} d ${h % 24} h`;
}

/** An execution still marked running for more than a day: its server probably stopped (never unlocked) */
export function isStale(status: string, created: string | null, now: Date): boolean {
  if (status !== 'R' || !created) return false;
  const t = Date.parse(created);
  return !isNaN(t) && now.getTime() - t > DAY;
}

/** Pretty JSON when the text is a JSON object or array, else null */
export function prettyJson(text: string | null | undefined): string | null {
  const t = (text ?? '').trim();
  if (!(t.startsWith('{') || t.startsWith('['))) return null;
  try {
    return JSON.stringify(JSON.parse(t), null, 2);
  } catch {
    return null;
  }
}

/** Stored record key "C_Order_ID,1000123" -> "C_Order_ID 1000123"; composite keys are kept readable */
export function readableRecordId(recordId: string | null | undefined): string {
  if (!recordId) return '';
  const parts = recordId.split(',');
  if (parts.length % 2 !== 0) return recordId;
  const out: string[] = [];
  for (let i = 0; i < parts.length; i += 2) out.push(`${parts[i]} ${parts[i + 1]}`);
  return out.join(' / ');
}

/** Period of the record changes made by an execution: from the minute it started to the minute after its end */
export function executionChangesWindow(created: string, updated: string | null, now: Date): { from: string; to: string } {
  const start = new Date(Date.parse(created));
  const endMs = updated ? Date.parse(updated) : now.getTime();
  const end = new Date(Math.max(endMs, start.getTime()) + 60_000);
  return { from: toLocalDateTime(start), to: toLocalDateTime(end) };
}
